/**
 * Unified Communication Timeline Layer
 * 
 * MODULE OWNERSHIP: Timeline domain (see moduleOwnership.ts)
 * RESPONSIBILITY: Consolidate all communication events into a single timeline per job.
 * TEMPLATE SCOPE: Job lifecycle templates (deposit, install, completion)
 * 
 * Does NOT own: automation rules (automationEngine), lead response templates
 * (pipelineIntelligence), or follow-up logic (unifiedFollowUp).
 * 
 * Derives from existing data — does NOT duplicate storage.
 */

import type { Job, Issue, ActivityEntry, Invoice, Payment } from "./types";
import type { InstallerWrapRecord } from "./installerWrapEngine";
import type { Message } from "./communicationTypes";
import type { Conversation } from "./communicationTypes";

// ── Communication Event Type ──

export type CommEventType = "sms" | "email" | "call" | "note" | "system";
export type CommEventActor = "system" | "admin" | "installer" | "customer";
export type CommEventStatus = "sent" | "delivered" | "failed" | "read";
export type CommEventDirection = "inbound" | "outbound";

export interface CommunicationEvent {
  id: string;
  jobId: string;
  timestamp: string;
  type: CommEventType;
  direction?: CommEventDirection;
  status?: CommEventStatus;
  subject?: string;
  message: string;
  actor: CommEventActor;
  metadata?: Record<string, unknown>;
}

// ── System Event Type ──

export type SystemEventType =
  | "job_created"
  | "deposit_requested"
  | "deposit_paid"
  | "scheduled"
  | "install_started"
  | "wrap_submitted"
  | "install_completed"
  | "qa_passed"
  | "payment_collected"
  | "issue_created"
  | "issue_resolved"
  | "stage_changed"
  | "invoice_created"
  | "scheduling_change"
  | "qa_submitted"
  | "admin_override"
  | "contractor_unassigned"
  | "contractor_interest_submitted"
  | "contractor_interest_closed"
  | "offer_created"
  | "offer_accepted"
  | "offer_declined"
  | "offer_expired"
  | "offer_withdrawn"
  | "offer_superseded"
  | "offer_extended"
  | "offer_window_changed";

export interface SystemEvent {
  type: SystemEventType;
  jobId: string;
  timestamp: string;
  payload?: Record<string, unknown>;
}

// ── Communication Event Filter ──

export type CommFilterCategory = "all" | "messages" | "calls" | "system" | "payments";

export const COMM_FILTER_LABELS: Record<CommFilterCategory, string> = {
  all: "All",
  messages: "Messages",
  calls: "Calls",
  system: "System Events",
  payments: "Payments",
};

// ── Derive unified timeline from existing data ──

function actorFromNoteType(noteType?: string): CommEventActor {
  if (noteType === "customer") return "customer";
  if (noteType === "crew") return "installer";
  if (noteType === "system") return "system";
  return "admin";
}

function activityToEvent(jobId: string, entry: ActivityEntry): CommunicationEvent {
  const isPayment = entry.action.toLowerCase().includes("payment") || entry.action.toLowerCase().includes("invoice") || entry.action.toLowerCase().includes("deposit");
  return {
    id: `act-${entry.id}`,
    jobId,
    timestamp: entry.date + "T12:00:00Z",
    type: isPayment ? "system" : "note",
    message: entry.action,
    actor: actorFromNoteType(entry.noteType),
    metadata: { source: "activity", noteType: entry.noteType },
  };
}

function wrapToEvent(wrap: InstallerWrapRecord): CommunicationEvent {
  const outcomeLabel = wrap.outcome === "completed_today" ? "Job completed" :
    wrap.outcome === "partial_reschedule" ? "Partial — return visit needed" :
    wrap.outcome === "blocked_issue" ? "Blocked by issue" :
    wrap.outcome === "customer_delay" ? "Customer delay" : wrap.outcome;

  return {
    id: `wrap-${wrap.id}`,
    jobId: wrap.jobId,
    timestamp: wrap.timestamp,
    type: "system",
    subject: "Installer Wrap Submitted",
    message: `${outcomeLabel} — ${wrap.completionPercent}% complete. ${wrap.progressNotes || ""}`.trim(),
    actor: "installer",
    metadata: { source: "wrap", outcome: wrap.outcome, completionPercent: wrap.completionPercent },
  };
}

function issueToEvent(issue: Issue): CommunicationEvent {
  return {
    id: `issue-${issue.id}`,
    jobId: issue.jobId,
    timestamp: issue.dateCreated + "T12:00:00Z",
    type: "system",
    subject: `Issue: ${issue.issueType}`,
    message: issue.description,
    actor: issue.source === "Customer" ? "customer" : "admin",
    metadata: { source: "issue", severity: issue.severity, status: issue.status },
  };
}

function messageToEvent(jobId: string, msg: Message): CommunicationEvent {
  return {
    id: `msg-${msg.id}`,
    jobId,
    timestamp: msg.timestamp,
    type: msg.channel === "call" ? "call" : msg.channel === "note" ? "note" : "sms",
    direction: msg.direction === "inbound" ? "inbound" : "outbound",
    status: msg.deliveryStatus === "delivered" ? "delivered" : msg.deliveryStatus === "failed" ? "failed" : "sent",
    message: msg.body,
    actor: msg.direction === "inbound" ? "customer" : "admin",
    metadata: { source: "message", channel: msg.channel, callDuration: msg.callDuration, callOutcome: msg.callOutcome },
  };
}

function jobStageEvents(job: Job): CommunicationEvent[] {
  const events: CommunicationEvent[] = [];
  const financials = job.financials;

  // Job created — use first activity entry date as proxy
  const firstActivity = job.activityHistory[0];
  if (firstActivity) {
    events.push({
      id: `stage-created-${job.id}`,
      jobId: job.id,
      timestamp: firstActivity.date + "T12:00:00Z",
      type: "system",
      subject: "Job Created",
      message: `Job created for ${job.customerName} — ${job.projectType}`,
      actor: "system",
      metadata: { source: "stage", event: "job_created" },
    });
  }

  // Scheduled
  if (job.scheduledInstallDate) {
    events.push({
      id: `stage-scheduled-${job.id}`,
      jobId: job.id,
      timestamp: job.scheduledInstallDate + "T08:00:00Z",
      type: "system",
      subject: "Install Scheduled",
      message: `Installation scheduled for ${job.scheduledInstallDate}`,
      actor: "system",
      metadata: { source: "stage", event: "scheduled" },
    });
  }

  // Deposit
  const depositLine = financials?.paymentPlan?.lineItems?.find(li => li.dueAt === "pre_schedule" && li.status === "paid");
  if (depositLine) {
    events.push({
      id: `stage-deposit-${job.id}`,
      jobId: job.id,
      timestamp: firstActivity?.date ? firstActivity.date + "T12:00:00Z" : new Date().toISOString(),
      type: "system",
      subject: "Deposit Received",
      message: `Deposit of $${depositLine.amount.toLocaleString()} received`,
      actor: "system",
      metadata: { source: "stage", event: "deposit_paid", amount: depositLine.amount },
    });
  }

  // Invoices
  financials?.invoices?.forEach((inv: Invoice) => {
    events.push({
      id: `inv-${inv.id}`,
      jobId: job.id,
      timestamp: inv.issuedAt || new Date().toISOString(),
      type: "system",
      subject: `Invoice: ${inv.type}`,
      message: `$${inv.amount.toLocaleString()} — ${inv.status}`,
      actor: "system",
      metadata: { source: "invoice", invoiceType: inv.type, status: inv.status, event: "invoice_created" },
    });
  });

  // Payments
  financials?.payments?.forEach((pmt: Payment) => {
    events.push({
      id: `pmt-${pmt.id}`,
      jobId: job.id,
      timestamp: pmt.timestamp,
      type: "system",
      subject: "Payment Received",
      message: `$${pmt.amount.toLocaleString()} via ${pmt.method}`,
      actor: "customer",
      metadata: { source: "payment", method: pmt.method, event: "payment_collected" },
    });
  });

  return events;
}

export function buildCommunicationTimeline(
  job: Job,
  issues: Issue[],
  wrapRecords: InstallerWrapRecord[],
  conversations: Conversation[],
  messages: Message[],
  automationEvents?: CommunicationEvent[],
): CommunicationEvent[] {
  const events: CommunicationEvent[] = [];

  // Activity history
  job.activityHistory.forEach(entry => events.push(activityToEvent(job.id, entry)));

  // Wraps
  wrapRecords.filter(w => w.jobId === job.id).forEach(w => events.push(wrapToEvent(w)));

  // Issues
  issues.filter(i => i.jobId === job.id).forEach(i => events.push(issueToEvent(i)));

  // Stage events
  events.push(...jobStageEvents(job));

  // Messages from conversations linked to this job
  const jobConvIds = conversations.filter(c => c.linkedJobId === job.id).map(c => c.id);
  messages.filter(m => jobConvIds.includes(m.conversationId)).forEach(m => events.push(messageToEvent(job.id, m)));

  // Automation execution events
  if (automationEvents) {
    automationEvents
      .filter(e => e.jobId === job.id)
      .forEach(e => events.push(e));
  }

  // Deduplicate by id
  const seen = new Set<string>();
  const deduped = events.filter(e => {
    if (seen.has(e.id)) return false;
    seen.add(e.id);
    return true;
  });

  // Sort chronologically
  return deduped.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
}

export function filterCommunicationEvents(events: CommunicationEvent[], filter: CommFilterCategory): CommunicationEvent[] {
  if (filter === "all") return events;
  if (filter === "messages") return events.filter(e => e.type === "sms" || e.type === "email" || e.type === "note");
  if (filter === "calls") return events.filter(e => e.type === "call");
  if (filter === "system") return events.filter(e => e.type === "system" && !e.metadata?.source?.match(/invoice|payment/));
  if (filter === "payments") return events.filter(e => e.metadata?.source === "invoice" || e.metadata?.source === "payment" || e.metadata?.event === "deposit_paid");
  return events;
}

// ── Message Template Engine ──

export interface MessageTemplate {
  id: string;
  name: string;
  channel: "sms" | "email";
  template: string;
  variables: string[];
}

export const MESSAGE_TEMPLATES: MessageTemplate[] = [
  {
    id: "tpl-deposit",
    name: "Deposit Request",
    channel: "sms",
    template: "Hi {{customerName}}, this is {{repName}} from Koredor. To confirm your project, we need to collect your deposit of {{depositAmount}}. Please call or text back to arrange payment. Thank you!",
    variables: ["customerName", "repName", "depositAmount"],
  },
  {
    id: "tpl-install-confirm",
    name: "Install Confirmation",
    channel: "sms",
    template: "Hi {{customerName}}, your installation is confirmed for {{installDate}}. Our crew will arrive between 8–10 AM. Please ensure access to the work area. Reply YES to confirm or call to reschedule.",
    variables: ["customerName", "installDate"],
  },
  {
    id: "tpl-install-reminder",
    name: "Install Reminder",
    channel: "sms",
    template: "Hi {{customerName}}, this is a reminder that your installation is tomorrow, {{installDate}}. Our team will arrive between 8–10 AM. See you then!",
    variables: ["customerName", "installDate"],
  },
  {
    id: "tpl-delay",
    name: "Delay Notification",
    channel: "sms",
    template: "Hi {{customerName}}, we need to let you know about a change to your project schedule. {{delayReason}}. We'll follow up with a new date shortly. We apologize for the inconvenience.",
    variables: ["customerName", "delayReason"],
  },
  {
    id: "tpl-completion",
    name: "Completion Message",
    channel: "sms",
    template: "Hi {{customerName}}, great news — your project has been completed! If you have any questions or concerns, don't hesitate to reach out. Thank you for choosing Koredor!",
    variables: ["customerName"],
  },
  {
    id: "tpl-review",
    name: "Review Request",
    channel: "sms",
    template: "Hi {{customerName}}, thank you for choosing Koredor! We'd love to hear about your experience. Would you mind leaving a review? It helps us serve more homeowners like you.",
    variables: ["customerName"],
  },
];

export function resolveTemplate(template: string, variables: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => variables[key] || `{{${key}}}`);
}

// ── Automation Trigger Events ──
// AUTHORITY: automationEngine.ts owns all automation rule types and evaluation.
// These trigger labels are TIMELINE-SPECIFIC display helpers for install lifecycle events.
// They do NOT duplicate automationEngine logic — they map to display labels only.
// See src/data/moduleOwnership.ts for full module boundary documentation.

export type AutomationTriggerEvent =
  | "deposit_paid"
  | "install_scheduled"
  | "install_reminder_24h"
  | "wrap_incomplete"
  | "qa_passed"
  | "payment_outstanding";

export const INSTALL_TRIGGER_LABELS: Record<AutomationTriggerEvent, string> = {
  deposit_paid: "When deposit status changes",
  install_scheduled: "When install is scheduled",
  install_reminder_24h: "24 hours before install",
  wrap_incomplete: "When wrap shows incomplete install",
  qa_passed: "When QA is approved",
  payment_outstanding: "When payment is overdue",
};

// ── Job Summary Helper ──

export function getJobSummary(job: Job): string {
  const stage = job.currentStage;
  const financials = job.financials;
  const hasDeposit = financials?.paymentPlan?.lineItems?.some(li => li.dueAt === "pre_schedule" && li.status === "paid");
  const needsDeposit = financials?.paymentPlan?.lineItems?.some(li => li.dueAt === "pre_schedule" && li.status !== "paid");
  const hasSchedule = !!job.scheduledInstallDate;

  if (stage === "Completed") {
    const balanceDue = financials?.paymentPlan?.lineItems?.some(li => li.status !== "paid");
    if (balanceDue) return "Completed — awaiting final payment";
    return "Completed — fully paid";
  }

  if (stage === "QA Review") {
    return "Install complete — pending QA review";
  }

  if (stage === "In Progress") {
    return "Install in progress — returning tomorrow";
  }

  if (stage === "Scheduled Confirmed" || stage === "Ready for Scheduling") {
    if (!hasSchedule) return "Ready to schedule — awaiting date assignment";
    return `Install scheduled for ${job.scheduledInstallDate}`;
  }

  if (stage === "Ordered" || stage === "Ready for Ordering") {
    return "Materials ordered — awaiting delivery";
  }

  if (needsDeposit) return "Waiting on deposit before scheduling";
  if (!hasDeposit && !hasSchedule) return "Awaiting deposit to proceed";

  return `${stage} — in progress`;
}
